import { useCallback, useState, type FormEvent } from 'react';
import { Button } from '../atoms/Button/Button';
import { Input } from '../atoms/Input/Input';
import { Stack } from '../atoms/Stack/Stack';
import { Text } from '../atoms/Typography/Text';
import { Section } from '../templates/Section/Section';
import { ConfirmDialog } from '../ui/ConfirmDialog/ConfirmDialog';
import { useTranslation } from '../../i18n/useTranslation';
import { ApiError } from '../../lib/apiClient';
import { markAccountDeleted } from '../../lib/accountDeleted';
import { deleteOwnAccount, RATE_LIMITED, REAUTH_FAILED } from '../../services/accountService';
import './DeleteAccountSection.css';

const DELETED_ITEMS = ['account', 'aquariums', 'journal', 'photos', 'other'] as const;
const PASSWORD_INPUT_ID = 'delete-account-password';

export interface DeleteAccountSectionProps {
  className?: string;
}

/**
 * Settings "Danger zone": self-service account deletion (DELETE /api/v1/me).
 * The confirmation dialog explains what is deleted and kept, recommends a data
 * export first and asks for the password (re-authentication). On success the
 * account-deleted flag signs the user out (AuthProvider) and the login page
 * shows "Your account has been deleted."
 */
export function DeleteAccountSection({ className = '' }: DeleteAccountSectionProps) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const close = useCallback((): void => {
    if (isDeleting) return;
    setIsOpen(false);
    setPassword('');
    setError(null);
  }, [isDeleting]);

  const confirm = async (): Promise<void> => {
    if (password === '' || isDeleting) return;
    setIsDeleting(true);
    setError(null);
    try {
      await deleteOwnAccount(password);
      setPassword('');
      markAccountDeleted();
    } catch (err: unknown) {
      setIsDeleting(false);
      if (err instanceof ApiError && err.code === REAUTH_FAILED) {
        setError(t('settings.deleteAccountSection.errors.wrongPassword'));
      } else if (err instanceof ApiError && err.code === RATE_LIMITED) {
        setError(t('settings.deleteAccountSection.errors.rateLimited'));
      } else {
        setError(t('settings.deleteAccountSection.errors.generic'));
      }
    }
  };

  const onSubmit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    void confirm();
  };

  return (
    <Section
      title={t('settings.dangerZone')}
      description={t('settings.deleteAccountSection.description')}
      className={['delete-account-section', className].filter(Boolean).join(' ')}
    >
      <Stack gap="3" align="start">
        <Button variant="danger" onClick={() => setIsOpen(true)}>
          {t('settings.deleteAccountSection.openButton')}
        </Button>
      </Stack>

      <ConfirmDialog
        isOpen={isOpen}
        title={t('settings.deleteAccountSection.dialogTitle')}
        message={t('settings.confirmDeleteAccount')}
        confirmLabel={t('settings.deleteAccountSection.confirm')}
        cancelLabel={t('common.cancel')}
        onConfirm={() => void confirm()}
        onCancel={close}
        confirmDisabled={password === ''}
        isConfirming={isDeleting}
      >
        <Text weight="semibold" color="primary">
          {t('settings.deleteAccountSection.whatIsDeleted')}
        </Text>
        <ul className="delete-account-section__list">
          {DELETED_ITEMS.map((item) => (
            <li key={item}>
              <Text as="span" size="sm">
                {t(`settings.deleteAccountSection.items.${item}`)}
              </Text>
            </li>
          ))}
        </ul>
        <Text size="sm">{t('settings.deleteAccountSection.keptNote')}</Text>
        <Text size="sm" weight="medium" color="primary">
          {t('settings.deleteAccountSection.exportHint')}
        </Text>
        <form onSubmit={onSubmit} noValidate>
          <Input
            id={PASSWORD_INPUT_ID}
            type="password"
            autoComplete="current-password"
            label={t('settings.deleteAccountSection.passwordLabel')}
            helperText={t('settings.deleteAccountSection.passwordHelp')}
            error={error ?? undefined}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={isDeleting}
            required
            fullWidth
          />
        </form>
      </ConfirmDialog>
    </Section>
  );
}

export default DeleteAccountSection;
